/**
 * The stock value report. Spec 017, FR-1701 to FR-1713.
 *
 * Guy, 29 October 2026, Part 9: the value of the stock at the end of each
 * month, at the true value of that date. The database writes the lines
 * (`stock_value_report`, migration 20261030004102). This file adds them up, so
 * the screen, the file and the tests all use one sum.
 *
 * Value is on hand times the average cost, so reserved stock is in it
 * (FR-1701). An item with no cost has no value, and the totals say how many
 * units that leaves out (FR-1702).
 */
import { classWord } from './stock-class';

export interface StockValueLine {
  item_id: string | null;
  location_id: string | null;
  code: string;
  product: string | null;
  colour: string | null;
  size: string | null;
  class: string | null;
  location_code: string | null;
  on_hand: number;
  /** Null for a past day: the system keeps no history of what was reserved. */
  reserved: number | null;
  free: number | null;
  /** Whole cents. Null is "No cost yet". Absent for a caller without costs.view. */
  avg_cost_cents?: number | null;
  value_cents?: number | null;
}

export interface StockValueTotal {
  /** The class word or the location code. */
  name: string;
  lines: number;
  on_hand: number;
  /** Absent for a caller without costs.view. */
  value_cents?: number;
  no_cost_units?: number;
}

export interface StockValueTotals {
  by_class: StockValueTotal[];
  by_location: StockValueTotal[];
  grand: StockValueTotal;
}

/** The sentence beside a cost that is not there. FR-1702. */
export const NO_COST_YET = 'No cost yet';

function add(into: Map<string, StockValueTotal>, name: string, line: StockValueLine, costs: boolean) {
  const t = into.get(name) ?? { name, lines: 0, on_hand: 0, ...(costs ? { value_cents: 0, no_cost_units: 0 } : {}) };
  t.lines += 1;
  t.on_hand += line.on_hand;
  if (costs) {
    if (line.avg_cost_cents == null) t.no_cost_units! += line.on_hand;
    else t.value_cents! += line.value_cents ?? 0;
  }
  into.set(name, t);
}

/**
 * Totals by class, by location, and for all of it. FR-1702.
 *
 * `costs` false gives the counts only, for somebody without costs.view: the
 * value fields are absent, not zero.
 */
export function stockValueTotals(lines: StockValueLine[], costs: boolean): StockValueTotals {
  const byClass = new Map<string, StockValueTotal>();
  const byLocation = new Map<string, StockValueTotal>();
  const grand = new Map<string, StockValueTotal>();
  for (const l of lines) {
    add(byClass, classWord(l.class), l, costs);
    add(byLocation, l.location_code ?? 'No location', l, costs);
    add(grand, 'All stock', l, costs);
  }
  const sorted = (m: Map<string, StockValueTotal>) => [...m.values()].sort((a, b) => a.name.localeCompare(b.name));
  return {
    by_class: sorted(byClass),
    by_location: sorted(byLocation),
    grand: grand.get('All stock') ?? { name: 'All stock', lines: 0, on_hand: 0, ...(costs ? { value_cents: 0, no_cost_units: 0 } : {}) },
  };
}

/** A day as `YYYY-MM-DD`, or null when it is not one. */
export function readAsAt(raw: string | null | undefined): string | null | 'invalid' {
  if (raw == null || raw === '') return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return 'invalid';
  // A real day: 2026-02-30 rolls over to March, so the parts must come back.
  const [y, m, day] = raw.split('-').map(Number);
  const d = new Date(Date.UTC(y!, m! - 1, day!));
  if (d.getUTCFullYear() !== y || d.getUTCMonth() !== m! - 1 || d.getUTCDate() !== day) return 'invalid';
  return raw;
}

/** '30 Oct 2026' from '2026-10-30'. The day, with no time zone in the way. */
export function dayWords(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y!, m! - 1, d!)).toLocaleDateString('en-AU', {
    day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC',
  });
}
